<template>
  <div class="companies">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs :items="[{ text: $t('meta.containers'), name: 'company_containers' }, { text: container.name }]" />
      </template>
      <template v-slot:meta>
        <button @click="duplicateContainer" class="button button-blue button-small button-round">
          {{ $t('containers.duplicateContainer') }}
        </button>
        <button @click="removeContainer" class="button button-red button-small button-round">
          {{ $t('containers.delete') }}
        </button>
        <Link :to="`/containers/${container.uuid}`" class="button button-green button-small button-round">{{
          $t('containers.containerView')
        }}</Link>
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <LoadingSpinner v-if="loading" />
      <div v-else class="body">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('meta.baseData') }}</h3>
            <div class="header-meta"></div>
          </header>
          <div class="content-body">
            <form @submit.prevent="updateContainer" class="form" method="post">
              <TextInputField :field="'name'" v-model="container.name" :labelTranslationKey="'meta.name'" />
              <GeoCoder :latitude="container.lat" :longitude="container.lng" @coordinates="getCoordinates" />
              <div class="form-wrap select-wrap">
                <label for="project">{{ $t('containers.project') }}</label>
                <select v-model="container.project_uuid" class="select-wrap">
                  <option v-for="project in projects" :value="project.uuid" :key="project.uuid">
                    {{ project.name }}
                  </option>
                </select>
              </div>

              <!-- FIXME Check selects that may not have areas and zones -->
              <div v-if="companyUser.company_type == 'industry'">
                <div class="form-wrap select-wrap">
                  <label for="site">{{ $t('meta.site') }}</label>
                  <select v-model="container.site_uuid" @change="getAreas" class="select-wrap">
                    <option v-for="site in sites" :value="site.uuid" :key="site.uuid">
                      {{ site.name }}
                    </option>
                  </select>
                </div>

                <div v-if="container.site_uuid" class="form-wrap select-wrap">
                  <label for="area">{{ $t('meta.area') }}</label>
                  <select v-model="container.area_uuid" @change="getZones" class="select-wrap">
                    <option v-for="area in areas" :value="area.uuid" :key="area.uuid">
                      {{ area.name }}
                    </option>
                  </select>
                </div>

                <div v-if="container.area_uuid" class="form-wrap select-wrap">
                  <label for="zone">{{ $t('meta.zone') }}</label>
                  <select v-model="container.zone_uuid" class="select-wrap">
                    <option v-for="zone in zones" :value="zone.uuid" :key="zone.uuid">
                      {{ zone.name }}
                    </option>
                  </select>
                </div>
              </div>

              <BooleanRadioField
                v-if="companyUser.company_type == 'industry'"
                v-model="container.replace_plant_area"
                :field="'replace_plant_area'"
                name="'replace_plant_area'"
              />

              <TextInputField
                field="type_name"
                v-model="container.type_name"
                :labelTranslationKey="'containerTypes.containerTypeName'"
              />

              <TextInputField
                field="volume"
                type="number"
                step="any"
                v-model="container.type_volume"
                :modelTranslationKey="'containerTypes'"
              />

              <input type="submit" :value="$t('meta.save')" class="button button-green button-100 button-round" />
            </form>
          </div>
        </div>
        <OptionalData :container="container" @refreshData="getData" />
        <Identifier :container="container" @refreshData="getData" />
        <Sensors :container="container" @refreshData="getData" />
        <EmptyTemplates :container="container" @refreshData="getData" />
        <Images :container="container" />
        <UvvChecks :container="container" />

        <History :history="history" @getVersionsData="getVersions" />
      </div>
    </div>
  </div>
</template>

<script>
import OptionalData from '@/views/companies/containers/OptionalData.vue'
import EmptyTemplates from '@/views/companies/containers/empty_templates/EmptyTemplates.vue'
import Identifier from '@/views/companies/containers/identifiers/Identifier.vue'
import Images from '@/views/companies/containers/images/Images.vue'
import Sensors from '@/views/companies/containers/sensors/Sensors.vue'
import UvvChecks from '@/views/companies/containers/uvv_checks/UvvChecks.vue'

import BreadCrumbs from '@/components/BreadCrumbs'
import History from '@/components/History.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav.vue'
import BooleanRadioField from '@/components/container/BooleanRadioField.vue'
import TextInputField from '@/components/container/TextInputField.vue'
import GeoCoder from '@/components/geocoder'
import dateUtils from '@/components/mixins/dateUtils'

import areasApi from '@/api/areas'
import containersApi from '@/api/container'
import containerGroupsApi from '@/api/containerGroups'
import projectsApi from '@/api/projects'
import sitesApi from '@/api/sites'
import zonesApi from '@/api/zones'

export default {
  name: 'CompanyContainerShow',
  inject: ['companyUser', 'showPopup'],
  data() {
    return {
      areas: [],
      container: {},
      container_groups: [],
      history: {
        versions: [],
        pagination: {
          page: 1,
          totalObjects: 0,
          perPage: 0,
        },
      },
      projects: [],
      sites: [],
      zones: [],
      loading: true,
    }
  },
  components: {
    BooleanRadioField,
    BreadCrumbs,
    CompanyNav,
    EmptyTemplates,
    GeoCoder,
    History,
    Images,
    Identifier,
    LoadingSpinner,
    SectionHeader,
    Sensors,
    TextInputField,
    OptionalData,
    UvvChecks,
  },
  mixins: [dateUtils],
  methods: {
    getCoordinates(value) {
      this.container.lat = value[1]
      this.container.lng = value[0]
    },
    async getData() {
      this.loading = true
      this.container = await containersApi.get(this.$route.params.container_uuid)
      if (this.container.site_uuid) {
        this.getAreas()
      }
      if (this.container.area_uuid) {
        this.getZones()
      }
      this.projects = await projectsApi.getAll(this.companyUser.company_uuid)
      this.sites = await sitesApi.getAllForCompany(this.companyUser.company_uuid)
      this.getVersions(1)
      this.loading = false
    },
    async getVersions(page) {
      const currentPage = page || this.history.pagination.page
      const result = await containersApi.getVersions(this.$route.params.container_uuid, { page: currentPage })
      this.history.versions = result.versions
      this.history.pagination.totalObjects = result.pagination.total_objects
      this.history.pagination.perPage = result.pagination.per_page
    },
    updateContainer: async function () {
      const result = await containersApi.update(this.$route.params.container_uuid, this.container)
      this.updateContainerResponse(result)
    },
    async removeContainer() {
      const confirmAlert = await this.showPopup({ content: this.$t('containers.deleteConfirmation'), type: 'confirm' })
      if (!confirmAlert) return null

      const result = await containersApi.delete(this.container.uuid)
      const notify = (noteType, text) =>
        this.$notify({
          group: 'alert',
          type: noteType,
          text: text,
        })

      if (result.success) {
        notify('success', this.$t('containers.deleted'))
        this.$router.push({ name: 'company_containers' })
      }
    },
    async duplicateContainer() {
      const newContainerName = await this.showPopup({
        title: this.$t('containers.duplicateContainer'),
        content: this.$t('containers.duplicateContainerConfirmation'),
        type: 'submit',
        defaultInputValue: `${this.container.name} - ${this.$t('meta.duplicate')}`,
      })
      if (!newContainerName) return

      const attributesToFilter = [
        'created_at',
        'empty_templates',
        'identifier',
        'images',
        'internal_id',
        'name',
        'name_plate',
        'plate_description',
        'plate_serial_number',
        'plate_year,',
        'sensors',
        'updated_at',
        'uuid',
        'uvv_checks',
      ]

      const filteredContainer = Object.keys(this.container)
        .filter(key => !attributesToFilter.includes(key))
        .reduce((obj, key) => {
          return {
            ...obj,
            [key]: this.container[key],
          }
        }, {})

      const containerDuplicate = { ...filteredContainer, name: newContainerName }

      const response = await containersApi.create(containerDuplicate)
      if (response.success) {
        await this.$router.push(`/company/containers/${response.data.container.uuid}`)
        await this.$router.go()
      }
    },
    async getAreas() {
      this.areas = await areasApi.getAll(this.container.site_uuid)
    },
    async getGroups() {
      const result = await containerGroupsApi.getAll({
        company_uuid: this.companyUser.company_uuid,
      })
      this.container_groups = result.containerGroups
    },
    async getZones() {
      this.zones = await zonesApi.getAllInArea(this.container.area_uuid)
    },
    updateContainerResponse(result) {
      if (result) {
        this.getData()
      }
    },
  },
  async mounted() {
    await Promise.all([this.getData(), this.getGroups()])
  },
}
</script>
